/* eslint-disable max-len */
import { useRef, useEffect } from 'react';
import {
  Button,
} from '@mui/material';
import { Editor } from 'react-draft-wysiwyg';
import ColorPicker from './custom/ColorPicker';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import './TextEditor.scss';
import { FONT_FORMAT_LIST, FONT_SIZE_LIST } from '../../constants/TextEditorConstants';
import ObjectUtils from '../../utils/ObjectUtils';
import DomUtils from '../../utils/DomUtils';
import { filterUndefined } from '../../utils/FilterUndefinedObject';

/**
 * A Wysiwyg text editor
 */
const defaultProps = {
  focus: false,
  onEditorStateChange: () => {},
  onApply: () => {},
  onRemove: () => {},
  editorState: null,
  disabledApply: false,
  disabledRemove: false,
  className: '',
  disabled: false,
  fontFamilyList: [],
  additionalActionChildren: null,
  includeInlineOption: true,
  includeTextAlign: true,
  toolbarCustomButtons: [],
  showApplyButton: true,
  showRemoveTextButton: true,
  selectAll: false,
  stripPastedStyles: true,
};
export default function TextEditor(props) {
  const {
    focus,
    editorState,
    onEditorStateChange,
    onApply,
    onRemove,
    disabledApply,
    disabledRemove,
    className,
    disabled,
    fontFamilyList,
    additionalActionChildren,
    includeInlineOption,
    includeTextAlign,
    toolbarCustomButtons,
    showApplyButton,
    showRemoveTextButton,
    stripPastedStyles,
    selectAll,
  } = { ...defaultProps, ...filterUndefined(props) };
  const editorRef = useRef(null);

  const setEditorReference = (ref) => {
    editorRef.current = ref;
    if (focus && ref) {
      ref.focus();
    }
  };

  useEffect(() => {
    if (editorRef.current && selectAll) {
      editorRef.current.focus();
      ObjectUtils.setTimeout(() => {
        document.execCommand('selectAll', false, null);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorRef.current]);

  ObjectUtils.setTimeout(() => {
    DomUtils.removeAttributeFromElements('[aria-selected="false"]');
    DomUtils.removeAttributeFromElements('[aria-expanded="false"]');
    DomUtils.removeAttributeFromElements('[aria-selected="true"]');

    const dropdown = document.querySelectorAll('.rdw-dropdown-wrapper');
    dropdown.forEach((element) => {
      element.removeAttribute('aria-expanded');
    });

    const colorPickerElements = document.querySelectorAll('[data-test="color-picker"]');
    colorPickerElements.forEach((element) => {
      element.removeAttribute('aria-label');
      element.removeAttribute('aria-expanded');
    });
  }, 100);

  const options = ['fontFamily', 'fontSize'];
  if (includeInlineOption) {
    options.push('inline');
  }

  options.push('colorPicker');
  if (includeTextAlign) {
    options.push('textAlign');
  }
  return (
    <div className={`text-editor-component-root ${className}`}>
      <Editor
        readOnly={disabled}
        editorState={editorState}
        editorRef={setEditorReference}
        onEditorStateChange={onEditorStateChange}
        wrapperClassName='text-editor-component'
        editorClassName='text-editor-editor'
        stripPastedStyles={stripPastedStyles}
        // handlePastedText={() => false}
        toolbar={{
          options,
          inline: {
            options: FONT_FORMAT_LIST,
            bold: { className: 'bordered-option-classname' },
            italic: { className: 'bordered-option-classname' },
            underline: { className: 'bordered-option-classname' },
          },
          colorPicker: {
            popupClassName: 'text-editor-color-picker',
            component: ColorPicker,
          },

          blockType: {
            className: 'bordered-option-classname',
          },
          fontSize: {
            options: FONT_SIZE_LIST,
            className: 'bordered-option-classname text-editor-font-size-display',
          },
          fontFamily: {
            options: [
              ...fontFamilyList,
            ],
            className: 'text-editor-font-family-display',
            dropdownClassName: 'bordered-option-classname text-editor-font-family-list',
          },
          textAlign: {
            left: { className: 'text-editor-align-left' },
            center: { className: 'text-editor-align-center' },
            right: { className: 'text-editor-align-right' },
            justify: { className: 'text-editor-align-justify' },
          },
        }}
        toolbarCustomButtons={toolbarCustomButtons}
      />
      <div className='text-editor-actions'>
        <div className='additional-actions-container'>
          {additionalActionChildren}
        </div>
        <div className='actions-container'>
          {showApplyButton && (
            <Button
              variant='contained'
              className='btn-primary margin-right-medium vizzle-text-editor-apply'
              onClick={onApply}
              disabled={disabledApply || disabled}
              data-test='apply-text-action'
            >
              Apply
            </Button>
          )}

          {showRemoveTextButton && (
            <Button
              variant='contained'
              className='btn-primary vizzle-text-editor-remove'
              disabled={disabledRemove || disabled}
              onClick={onRemove}
              data-test='remove-text-action'
            >
              Remove
            </Button>
          )}

        </div>
      </div>
    </div>
  );
}
